/**
 * Admin Route Component
 * 
 * This component handles the conditional rendering at /admin route:
 * - Shows login page if user is not authenticated
 * - Shows dashboard or other pages with layout if user is authenticated
 * 
 * This keeps the URL at /admin regardless of login status.
 */
import React, { useEffect, useState } from 'react';
import AdminLogin from './login/login';
import AdminChangePassword from './login/ChangePasswordForm';
import AdminLayout from './common/layout/AdminLayout';
import { SecretExpiryProvider } from '../../contexts/SecretExpiryContext';
import Dashboard from './homePage/Dashboard';
import EmpManagement, { type Employee } from './empManagement/empManagement';
import ApplicationHome from './applicationManagement/applicationHome/ApplicationHome';
import DocumentManagement from './documentManagement/DocumentManagement';
import CheckSubmissions from './documentManagement/checkSubmissions/CheckSubmissions';
import type { Submission } from './documentManagement/types';
import SettingsHome from './settings/settingsHome';
import AdminManagement from './adminManagement/adminManagement';
import AdminMessages from './messages/AdminMessages';
import { installAdminFetchInterceptor } from '../../utils/adminFetch';
import { AdminChatUnreadProvider } from '../../contexts/AdminChatUnreadContext';
import {
  clearAdminSession,
  isAdminAuthenticated,
  mustChangeAdminPassword,
  setAdminSession,
} from './auth/adminAuthStorage';
import { fetchAdminMe, logoutAdmin } from '../../services/adminAuthApi';

type NavTab = 'dashboard' | 'employees' | 'invitations' | 'documents' | 'messages' | 'admins' | 'settings';

const AdminRoute: React.FC = () => {
  // Start with false - always show login page initially
  // Authentication state will be updated after successful login
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<NavTab>('dashboard');
  const [openInvitationErrorsView, setOpenInvitationErrorsView] = useState<boolean>(false);
  const [openCreateInvitationView, setOpenCreateInvitationView] = useState<boolean>(false);
  const [rehirePrefillParticipant, setRehirePrefillParticipant] = useState<{
    email: string;
    name: string;
  } | null>(null);
  const [archiveSubmission, setArchiveSubmission] = useState<{
    submission: Submission;
    invitationId?: string;
    finalizedAt?: string;
    needsFinalize?: boolean;
  } | null>(null);
  const [empMgmtRefreshKey, setEmpMgmtRefreshKey] = useState(0);
  const [messagesInitialEmail, setMessagesInitialEmail] = useState<string | undefined>();
  const [messagesInitialInvitationId, setMessagesInitialInvitationId] = useState<string | undefined>();

  const handleStartRehire = (participant: { email: string; name: string }) => {
    setArchiveSubmission(null);
    setRehirePrefillParticipant(participant);
    setActiveTab('invitations');
    setOpenCreateInvitationView(true);
  };

  /**
   * Restore the server session (cookie) on load; localStorage is only a UI hint.
   */
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      try {
        const me = await fetchAdminMe();
        if (cancelled) return;
        setAdminSession(me.email, me.mustChangePassword);
        setIsAuthenticated(true);
        setMustChangePassword(me.mustChangePassword);
      } catch {
        if (cancelled) return;
        clearAdminSession();
        setIsAuthenticated(false);
        setMustChangePassword(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };

    void restore();

    const handleAuthChange = () => {
      void (async () => {
        if (!isAdminAuthenticated()) {
          setIsAuthenticated(false);
          setMustChangePassword(false);
          return;
        }
        try {
          const me = await fetchAdminMe();
          if (cancelled) return;
          setAdminSession(me.email, me.mustChangePassword);
          setIsAuthenticated(true);
          setMustChangePassword(me.mustChangePassword);
        } catch {
          if (cancelled) return;
          clearAdminSession();
          setIsAuthenticated(false);
          setMustChangePassword(false);
        }
      })();
    };
    const handleExpired = () => {
      void logoutAdmin();
      clearAdminSession();
      setIsAuthenticated(false);
      setMustChangePassword(false);
      setActiveTab('dashboard');
      setArchiveSubmission(null);
    };

    window.addEventListener('admin-auth-change', handleAuthChange);
    window.addEventListener('admin-session-expired', handleExpired);

    return () => {
      cancelled = true;
      window.removeEventListener('admin-auth-change', handleAuthChange);
      window.removeEventListener('admin-session-expired', handleExpired);
    };
  }, []);

  /** Attach CSRF + session cookie on API calls. */
  useEffect(() => {
    if (!isAuthenticated) return;
    return installAdminFetchInterceptor();
  }, [isAuthenticated]);

  /**
   * Handles tab change from navbar
   */
  const handleTabChange = (tab: NavTab) => {
    setActiveTab(tab);
    if (tab !== 'invitations') {
      setOpenInvitationErrorsView(false);
    }
  };

  const handleLogout = () => {
    void logoutAdmin();
    clearAdminSession();
    setActiveTab('dashboard');
    setArchiveSubmission(null);
    window.dispatchEvent(new Event('admin-auth-change'));
  };

  const adminEmail = typeof window !== 'undefined'
    ? localStorage.getItem('admin-email')?.trim() || ''
    : '';
  const headerUserName = adminEmail || '管理者';

  /**
   * Called when user clicks the bounce notification on dashboard; switch to invitations and open 招待送信エラー view
   */
  const handleNavigateToInvitationErrors = () => {
    setActiveTab('invitations');
    setOpenInvitationErrorsView(true);
  };

  /**
   * Called when user clicks the "registered not yet sent" notification; switch to invitations and open 招待を作成 UI
   */
  const handleNavigateToCreateInvitation = () => {
    setActiveTab('invitations');
    setOpenCreateInvitationView(true);
  };

  /**
   * Called when user clicks the "new submissions to check" notification; switch to 書類管理 (documents) tab
   */
  const handleNavigateToDocuments = () => {
    setActiveTab('documents');
  };

  const handleNavigateToMessages = () => {
    setActiveTab('messages');
  };

  const handleOpenMessagesForEmployee = (email: string, invitationId?: string) => {
    setMessagesInitialEmail(email.trim());
    setMessagesInitialInvitationId(invitationId?.trim() || undefined);
    setActiveTab('messages');
  };

  /**
   * Renders the appropriate content based on active tab
   */
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <Dashboard
            onNavigateToInvitationErrors={handleNavigateToInvitationErrors}
            onNavigateToCreateInvitation={handleNavigateToCreateInvitation}
            onNavigateToDocuments={handleNavigateToDocuments}
            onNavigateToMessages={handleNavigateToMessages}
          />
        );
      case 'employees':
        return (
          <EmpManagement
            key={empMgmtRefreshKey}
            onStartRehire={handleStartRehire}
            onEmployeeClick={(employee: Employee) => {
              if (!employee.email?.trim()) return;
              const archiveInvitationId = employee.archiveInvitationId?.trim() || undefined;
              const finalizeInvitationId =
                employee.needsFinalize === true
                  ? employee.invitationId?.trim() || archiveInvitationId
                  : archiveInvitationId;
              setArchiveSubmission({
                submission: {
                  id: employee.id,
                  status: 'completed',
                  empName: employee.name,
                  email: employee.email,
                  requestDate: employee.registrationDate,
                  submittedDate: employee.registrationDate,
                  submittedTime: '',
                  operationType: 'open',
                },
                invitationId: finalizeInvitationId,
                finalizedAt: employee.finalizedAt,
                needsFinalize: employee.needsFinalize === true,
              });
            }}
          />
        );
      case 'invitations':
        return (
          <ApplicationHome
            onCreateInvitation={() => {}}
            onErrorClick={() => {}}
            initialShowErrorsView={openInvitationErrorsView}
            onErrorsViewOpened={() => setOpenInvitationErrorsView(false)}
            initialShowCreateInvitationView={openCreateInvitationView}
            onCreateInvitationViewOpened={() => setOpenCreateInvitationView(false)}
            initialRehireParticipant={rehirePrefillParticipant}
            onRehireParticipantConsumed={() => setRehirePrefillParticipant(null)}
            onOpenMessages={handleOpenMessagesForEmployee}
          />
        );
      case 'documents':
        return (
          <DocumentManagement
            onStartRehire={handleStartRehire}
            onOperationClick={() => {
              // Opening Check Submissions is handled inside DocumentManagement
            }}
          />
        );
      case 'messages':
        return (
          <AdminMessages
            initialSelectEmail={messagesInitialEmail}
            initialInvitationId={messagesInitialInvitationId}
            onInitialSelectConsumed={() => {
              setMessagesInitialEmail(undefined);
              setMessagesInitialInvitationId(undefined);
            }}
          />
        );
      case 'admins':
        return <AdminManagement />;
      case 'settings':
        return <SettingsHome />;
      default:
        return (
          <Dashboard
            onNavigateToInvitationErrors={handleNavigateToInvitationErrors}
            onNavigateToCreateInvitation={handleNavigateToCreateInvitation}
            onNavigateToDocuments={handleNavigateToDocuments}
            onNavigateToMessages={handleNavigateToMessages}
          />
        );
    }
  };

  /**
   * Determines if white background should be used based on active tab
   */
  const useWhiteBackground = activeTab !== 'dashboard';

  if (!authChecked) {
    return <AdminLogin />;
  }

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  if (mustChangePassword) {
    return <AdminChangePassword onLogout={handleLogout} />;
  }

  // Show content with layout if authenticated
  return (
    <SecretExpiryProvider>
      <AdminChatUnreadProvider>
      <AdminLayout
        activeTab={activeTab}
        onTabChange={handleTabChange}
        useWhiteBackground={useWhiteBackground}
        userName={headerUserName}
        onLogout={handleLogout}
      >
        {renderContent()}
        {archiveSubmission && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.35)', padding: 24, overflow: 'auto' }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              <CheckSubmissions
                mode="archive"
                submission={archiveSubmission.submission}
                archiveInvitationId={archiveSubmission.invitationId}
                archiveFinalizedAt={archiveSubmission.finalizedAt}
                onStartRehire={handleStartRehire}
                onClose={() => {
                  setArchiveSubmission(null);
                  setEmpMgmtRefreshKey((k) => k + 1);
                }}
              />
            </div>
          </div>
        )}
      </AdminLayout>
      </AdminChatUnreadProvider>
    </SecretExpiryProvider>
  );
};

export default AdminRoute;
